import { useCallback, useEffect, useRef, useState } from 'react'
import { get, patch, post } from '../lib/api'
import { t } from '../lib/i18n'
import type { User } from '../lib/auth'
import Icon from './Icon'

/**
 * The garage, which is also the leaderboard.
 *
 * One screen rather than two, because they are the same list read for two
 * reasons -- "which cars do we have" and "which one is winning" -- and a second
 * screen would only be the first one sorted differently. The order IS the
 * answer, so the rank is the first thing on every row.
 *
 * The score beside it is the habit score from shared/ranking.ts: win rate
 * weighted by how much racing backs it up. It is shown, not hidden, because a
 * ranking nobody can interrogate is a ranking that gets argued with.
 */

export interface Car {
  id: string
  name: string
  photo_id: string | null
  notes: string | null
  retired_at: number | null
  wins: number
  losses: number
  winRate: number
  score: number
  rank: number
}

export function carPhoto(car: Car): string | null {
  return car.photo_id ? `/api/files/${car.photo_id}` : null
}

export default function Garage({ user }: { user: User }) {
  const [cars, setCars] = useState<Car[]>([])
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [photo, setPhoto] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [showRetired, setShowRetired] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const mayRetire = user.capabilities.includes('operations.write')

  const load = useCallback(async () => {
    try {
      const r = await get<{ cars: Car[] }>(`/api/cars${showRetired ? '?retired=1' : ''}`)
      setCars(r.cars)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    }
  }, [showRetired])

  useEffect(() => { void load() }, [load])

  async function add(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setBusy(true)
    setError(null)
    try {
      /*
       * The photo goes up FIRST, and the car is created with its id.
       *
       * The other order -- create, then upload, then attach -- leaves a car with
       * no picture whenever the second request fails, and nothing on the screen
       * says which cars are waiting for one. Here a failed upload fails the
       * whole thing and the form still has what was typed in it.
       */
      let photoId: string | null = null
      if (photo) {
        const form = new FormData()
        form.set('file', photo)
        form.set('purpose', 'car')
        form.set('refTable', 'cars')
        /*
         * POST /api/files, not /api/uploads.
         *
         * `/api/uploads` is what the module is CALLED and it is not what the
         * route is: uploads.ts mounts the write at /api/files, beside the
         * /api/files/<id> that serves the bytes back. Posting to the module's
         * name answered 404, which this form reported as "that photo would not
         * upload" -- correctly, and with no clue that the file was never the
         * problem. Nothing in the type system connects a fetch() to a route
         * string; the only thing that catches it is uploading a photo.
         */
        const res = await fetch('/api/files', { method: 'POST', body: form, credentials: 'same-origin' })
        const uploaded = await res.json() as { id?: string; error?: string }
        if (!res.ok || !uploaded.id) throw new Error(uploaded.error ?? t('garage.photoFailed'))
        photoId = uploaded.id
      }

      await post('/api/cars', { name: name.trim(), photoId })
      setName('')
      setPhoto(null)
      if (fileInput.current) fileInput.current.value = ''
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  async function retire(car: Car, retired: boolean) {
    try {
      await patch(`/api/cars/${car.id}`, { retired })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    }
  }

  return (
    <section className="card">
      <h1>{t('garage.title')}</h1>

      {error && <p className="error" role="alert">{error}</p>}

      {cars.length === 0 ? (
        <p className="muted">{t('garage.empty')}</p>
      ) : (
        <ol className="garage">
          {cars.map((car) => (
            <li key={car.id} className={car.retired_at ? 'is-retired' : ''}>
              <span className="garage-rank">{car.rank}</span>
              {carPhoto(car)
                ? <img className="garage-photo" src={carPhoto(car)!} alt="" loading="lazy" />
                : <span className="garage-photo is-empty"><Icon name="car" size={22} /></span>}
              <span className="garage-name">
                {car.name}
                {car.retired_at && <span className="muted small"> · {t('garage.retired')}</span>}
                <span className="muted small garage-record">
                  {car.wins}–{car.losses}
                  {car.wins + car.losses > 0 && ` · ${Math.round(car.winRate)}%`}
                </span>
              </span>
              <span className="garage-score" title={t('garage.scoreHint')}>{car.score}</span>
              {mayRetire && (
                <button type="button" className="link" onClick={() => void retire(car, !car.retired_at)}>
                  {car.retired_at ? t('garage.unretire') : t('garage.retire')}
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      <button type="button" className="link" onClick={() => setShowRetired(!showRetired)}>
        {showRetired ? t('garage.hideRetired') : t('garage.showRetired')}
      </button>

      <h2>{t('garage.add')}</h2>
      <form onSubmit={add}>
        <label>
          {t('garage.carName')}
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required />
        </label>
        <label>
          {t('garage.photo')}
          {/* `capture` opens the camera straight away on a phone or tablet, which
              is where this is used: the car is in the other hand. */}
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
          />
        </label>
        <button type="submit" className="primary" disabled={busy || !name.trim()}>
          <Icon name="plus" size={16} /> {busy ? t('app.loading') : t('garage.add')}
        </button>
      </form>
    </section>
  )
}
